/**
 * PATH: apps/teams/src/config/tabs.ts
 * PURPOSE: Teams Hub tab configuration for the shared tab system.
 *
 * WHY: Browser-style tabs for Channels, Direct Messages, Notes, Meetings, People
 *      with route sync and keyboard shortcuts.
 */

import { createTabStore, type TabNavigationConfig } from '@quotech/ui'
import { Hash, MessageCircle, FileText, Video, Users } from 'lucide-react'

// ─── Tab Store ──────────────────────────────────────────────
export const useTeamsTabStore = createTabStore({
  hubId: 'teams',
  defaultTab: { path: '/channels', title: 'Channels', iconKey: 'Hash' },
})

// ─── Icon Map ───────────────────────────────────────────────
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Hash,
  MessageCircle,
  FileText,
  Video,
  Users,
}

// ─── Route Map ──────────────────────────────────────────────
const ROUTE_MAP: Record<string, { title: string; iconKey: string }> = {
  '/': { title: 'Channels', iconKey: 'Hash' },
  '/channels': { title: 'Channels', iconKey: 'Hash' },
  '/direct': { title: 'Direct Messages', iconKey: 'MessageCircle' },
  '/messages': { title: 'Direct Messages', iconKey: 'MessageCircle' },
  '/notes': { title: 'Notes', iconKey: 'FileText' },
  '/meetings': { title: 'Meetings', iconKey: 'Video' },
  '/people': { title: 'People', iconKey: 'Users' },
}

// ─── Config ─────────────────────────────────────────────────
export const TEAMS_TAB_CONFIG: TabNavigationConfig = {
  useStore: useTeamsTabStore,
  routeMap: ROUTE_MAP,
  dynamicRoutes: [
    {
      pattern: /^\/channels\/(.+)/,
      getMetadata: (path) => ({
        title: `Channel #${path.split('/')[2]}`,
        iconKey: 'Hash',
      }),
    },
    {
      pattern: /^\/messages\/(.+)/,
      getMetadata: (path) => ({
        title: `Channel #${path.split('/')[2]}`,
        iconKey: 'MessageCircle',
      }),
    },
  ],
  excludedPaths: ['/sso', '/login', '/settings'],
  defaultNewTabPath: '/channels',
  iconMap: ICON_MAP,
}
